import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { api } from './api'
import { useAuth } from './useAuth'

/**
 * Encerra a sessão depois de um tempo sem atividade, conforme os parâmetros do assinante.
 *
 * Antes essa contagem morava dentro do contador mm:ss que aparecia no cabeçalho, então a regra de
 * segurança dependia de um elemento visual estar montado. Aqui ela não tem interface: o cabeçalho
 * passou a mostrar data e hora, e a expiração continua valendo do mesmo jeito.
 *
 * "Atividade" é requisição à API — o interceptor reinicia a contagem a cada chamada, que é o
 * mesmo critério usado antes.
 */
export function useIdleSessionTimeout(
  minutes: number | undefined,
  enabled: boolean
): void {
  const { signOutBecauseOfInactivity } = useAuth()

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!enabled || !minutes || minutes <= 0) {
      return
    }

    const limit = minutes * 60 * 1000

    function schedule() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        toast.error('Sessão expirada')
        signOutBecauseOfInactivity()
      }, limit)
    }

    schedule()

    const interceptor = api.interceptors.request.use(config => {
      schedule()
      return config
    })

    return () => {
      api.interceptors.request.eject(interceptor)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [enabled, minutes, signOutBecauseOfInactivity])
}
